import { useState } from "react";

import { Box, Button, Menu, MenuItem, Stack } from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import DriveFileMoveIcon from "@mui/icons-material/DriveFileMove";
import BadgeIcon from "@mui/icons-material/Badge";

import PatientEditForm from "../../patients/components/PatientEditForm";
import MoveExaminationDialog from "./MoveExaminationDialog";

import { createUploadObject } from "../../../../helper-functions/shared/miscUtils";
import { normalizePatientRowForUpload } from "../../../../helper-functions/shared/patientDisplay";
import { updatePatient } from "../../../../helper-functions/api/patientQueries";
import { reassignExamination } from "../../../../helper-functions/api/examinationQueries";
import { PATIENT_NULLABLE_FIELDS } from "../../../../config/constants/Constants";

export default function ExaminationEditMenu(props) {
    const { examination, onPatientUpdated, fullWidth = false } = props;
    const [anchor, setAnchor] = useState(null);
    const open = Boolean(anchor);

    const close = () => setAnchor(null);

    const editEntry = () => {
        close();
        if (!examination?.id) return;
        props.browser.navigate(`/database/examinations/${examination.id}/edit`);
    };

    const editPatient = () => {
        close();
        const patient = examination?.patient;
        if (!patient?.id) return;

        props.showDialog({
            title: "Edit patient details",
            message: "Change the patient ID, name, or date of birth. Previous examinations stay on this same patient.",
            component: (
                <PatientEditForm
                    {...props}
                    row={patient}
                    callback={(confirm, options = {}) => {
                        props.setOpenDialog(false);
                        if (!confirm) return;

                        const cleanRow = normalizePatientRowForUpload(options.row);
                        const body = createUploadObject(
                            cleanRow,
                            ["form", "_*", "id", "created_on", "updated_on", "examinations"],
                            ["*"],
                            PATIENT_NULLABLE_FIELDS
                        );
                        updatePatient(props, { body, id: patient.id }, (response) => {
                            onPatientUpdated?.(response.body);
                        });
                    }}
                />
            )
        });
    };

    const moveExamination = () => {
        close();
        if (!examination?.id) return;

        props.showDialog({
            title: "Move to another patient",
            component: (
                <MoveExaminationDialog
                    {...props}
                    examination={examination}
                    onCancel={() => props.setOpenDialog(false)}
                    onConfirm={(patient) => {
                        props.setOpenDialog(false);
                        if (!patient?.id) return;
                        reassignExamination(props, { id: examination.id, patientId: patient.id }, () => {
                            props.browser.navigate(`/database/patients/${patient.id}`);
                        });
                    }}
                />
            )
        });
    };

    return (
        <Box sx={{ width: fullWidth ? "100%" : "auto" }}>
            <Button
                size="small"
                variant="contained"
                color="primary"
                fullWidth={fullWidth}
                startIcon={<EditIcon fontSize="small" />}
                onClick={(event) => setAnchor(event.currentTarget)}
                aria-haspopup="menu"
                aria-expanded={open ? "true" : undefined}>
                Edit
            </Button>
            <Menu anchorEl={anchor} open={open} onClose={close} anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }}>
                <MenuItem onClick={editEntry}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <EditIcon fontSize="small" />
                        <span>Edit Examination Entry</span>
                    </Stack>
                </MenuItem>
                <MenuItem onClick={editPatient} disabled={!examination?.patient?.id}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <BadgeIcon fontSize="small" />
                        <span>Edit Patient Details</span>
                    </Stack>
                </MenuItem>
                <MenuItem onClick={moveExamination}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <DriveFileMoveIcon fontSize="small" />
                        <span>Move to another patient</span>
                    </Stack>
                </MenuItem>
            </Menu>
        </Box>
    );
}
